import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { cn, Icon, Tooltip, useTranslate } from '@prenuvo/halo';
import { ChevronDown } from '@prenuvo/halo-icon';
import { useShallow } from 'zustand/react/shallow';

import { CARE_SERVICE_ORGANS_PARENT_KEY } from '@/api';
import { useCarePlanService, useStudyGuard } from '@/shared/guard';
import { ReportLink, ReportLinkKey, ReportLinkProgress } from '@/shared/model';
import { isEmpty, isNotEmpty } from '@/shared/util';
import { useReportValidationStore } from '@/store';

import { usePreviewScrollPosition } from '../../../hook';
import { generateReportingRoute } from '../../../util';
import { DEFAULT_EXPANDED_LINK_KEYS, NOT_AN_ORGAN } from './nav-menu.constant';
import { getStatusContent } from './nav-menu.util';
import { useNavMenu } from './nav-menu-context';
import { ProgressIndicator } from './progress-indicator';
import { useKiLabelCount } from './use-ki-label-count.hook';
import { useUpdateSubOrganStatus } from './use-update-sub-organ-status.hook';

interface NavMenuLinkProps {
  index: number;
  isSubmenuItem: boolean;
  link: ReportLink;
  onClick?: () => void;
}

export function NavMenuLink({ index, isSubmenuItem = false, link, onClick }: NavMenuLinkProps) {
  const { getStatus, setInCompleteLink } = useReportValidationStore(
    useShallow((state) => ({
      getStatus: state.getStatus,
      links: state.links,
      setInCompleteLink: state.setInCompleteLink,
    })),
  );

  const { linkItemRefs, onLinkSelect, onOrganSelect, selectedOrgan } = useNavMenu();
  const { studyId } = useStudyGuard();
  const { links } = useCarePlanService();
  const setPreviewScrollPosition = usePreviewScrollPosition();
  const getKeyImageLabelCount = useKiLabelCount();
  const translate = useTranslate();

  const [expandedMenus, setExpandedMenus] = useState<string[]>(DEFAULT_EXPANDED_LINK_KEYS);

  const isActive = link.key === selectedOrgan || link.sub?.some((sub) => sub.key === selectedOrgan);
  const isDisabled = false;
  const hasSubmenu = isNotEmpty(link.sub);
  const isExpanded = expandedMenus.includes(link.key);

  const organLink = links.find((linkData) => linkData.key === link.key);
  const subOrganLink = organLink?.sub ? organLink.sub.map((item) => item.key) : null;

  const organStatus = getStatus(link.key);

  const tooltipContent = link.disabled
    ? `${link.title} - ${translate('FCT.NAV_MENU.NOT_IN_SCAN')}`
    : `${link.title} ${getStatusContent(getStatus(link.key))}`;

  useUpdateSubOrganStatus(link.key, subOrganLink);

  const keyImageLabelCount = getKeyImageLabelCount(link.key);

  const toggleSubmenu = (key: string) => {
    setExpandedMenus((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const hasSubmenuStatus = () =>
    link.sub?.some((subLink) => getStatus(subLink.key)?.error || getStatus(subLink.key)?.warning);

  const hasSubmenuError = () => link.sub?.some((subLink) => getStatus(subLink.key)?.error);

  const hasSubmenuWarning = () => link.sub?.some((subLink) => getStatus(subLink.key)?.warning);

  const shouldShowIndicator = (linkKey: ReportLinkKey) => {
    const status = getStatus(linkKey);

    if (status?.warning || (hasSubmenu && hasSubmenuStatus())) {
      return true;
    }

    return status?.error || (hasSubmenu && hasSubmenuError());
  };

  const getIndicatorProgress = (linkKey: ReportLinkKey): null | ReportLinkProgress => {
    const status = getStatus(linkKey);

    if (status?.error || (hasSubmenu && hasSubmenuError())) {
      return 'missingInput';
    }

    if (!status?.error && (status?.warning || (hasSubmenu && hasSubmenuWarning()))) {
      return 'oncorad';
    }

    return null;
  };

  useEffect(() => {
    if (getIndicatorProgress(link.key) === 'missingInput') {
      setInCompleteLink(link.key, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [link.key, getIndicatorProgress(link.key)]);

  return (
    <div className="relative w-full overflow-hidden">
      <Link
        className={cn(
          'group inline-flex w-full items-center justify-center text-sm font-medium whitespace-nowrap text-neutral-400 hover:no-underline dark:text-neutral-400',
          {
            'dark:bg-stone-900': isActive && isSubmenuItem,
            'hover:bg-stone-900': isSubmenuItem,
            'text-neutral-800 dark:text-neutral-800': isDisabled || link.disabled,
          },
        )}
        data-testid="nav-menu-link"
        onClick={(e) => {
          if (hasSubmenu) {
            e.preventDefault();
            toggleSubmenu(link.key);
          } else {
            setPreviewScrollPosition();
            onOrganSelect(link.key);
            onLinkSelect?.(link.key);
            onClick?.();
          }
        }}
        to={generateReportingRoute(studyId, link.key)}
      >
        <Tooltip
          className={cn(
            'bg-stone-700 text-xs text-neutral-50 dark:bg-stone-700 dark:text-neutral-50',
            {
              hidden:
                (isEmpty(getStatusContent(getStatus(link.key))) && !link.disabled) || hasSubmenu,
            },
          )}
          content={tooltipContent}
          side="right"
        >
          <div
            className={cn(
              'static flex h-full w-full items-center justify-between hover:bg-stone-900 hover:text-neutral-400 dark:hover:bg-stone-900 dark:hover:text-neutral-400',
              {
                'bg-stone-900 text-neutral-50 hover:text-neutral-50 dark:bg-stone-900 dark:text-neutral-50 dark:hover:text-neutral-50':
                  isActive && (!hasSubmenu || !isExpanded || isExpanded),
                'text-neutral-50 hover:text-neutral-50 dark:text-neutral-50 dark:hover:text-neutral-50':
                  isActive && hasSubmenu && isExpanded,
                'text-neutral-800 dark:text-neutral-800':
                  isSubmenuItem && link.disabled && !isActive,
              },
            )}
            data-selected={`${isActive}`}
            ref={(el: HTMLDivElement | null) => {
              linkItemRefs.current[index] = el;
            }}
          >
            <span className="relative flex items-center">
              <div
                className={cn('absolute top-0 left-0 h-full w-1', {
                  'bg-green-500':
                    !NOT_AN_ORGAN.includes(link.key) && organStatus?.done && !organStatus?.error,
                  'bg-neutral-700':
                    !NOT_AN_ORGAN.includes(link.key) && (!organStatus?.done || organStatus?.error),
                })}
              />
              <span className="py-0.5 pr-2 pl-3 text-sm font-normal whitespace-normal no-underline">
                {link.title}
                {keyImageLabelCount > 0 && <span className="pl-2">{keyImageLabelCount}</span>}
              </span>
            </span>
            <div className="flex items-center">
              {(!hasSubmenu || (hasSubmenu && !isExpanded)) && shouldShowIndicator(link.key) && (
                <span className="ml-auto flex shrink-0 items-center">
                  <ProgressIndicator progress={getIndicatorProgress(link.key)} />
                </span>
              )}
              {hasSubmenu && (
                <Icon
                  className={{
                    root: cn(
                      'ml-2 h-4 w-4 fill-current transition-transform',
                      isExpanded && 'rotate-180',
                    ),
                  }}
                  source={ChevronDown}
                />
              )}
            </div>
          </div>
        </Tooltip>
      </Link>
      {hasSubmenu && isExpanded && (
        <div
          className={cn('flex flex-col gap-1 pt-1', {
            'ml-3': link.key === CARE_SERVICE_ORGANS_PARENT_KEY.NECK,
          })}
        >
          {link.sub?.map((subLink, subIndex) => (
            <NavMenuLink key={subLink.key} index={index + subIndex} isSubmenuItem link={subLink} />
          ))}
        </div>
      )}
    </div>
  );
}
